/* eslint-disable no-undef */
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FullHeaderLeftSideFullPanelLayout } from 'layouts/fullHeader.LeftSideFullPanel.layout';
import {
  StepDefinition,
  WizardShell,
  PaymentUnsuccessfulScreen
} from 'components/PremiumWizard';
import useBydaMemberLookupWizard, {
  formatFileSize
} from './hooks/useBydaMemberLookupWizard';
import useMapSketchRegistration from './hooks/useMapSketchRegistration';
import PriceStep from './components/PriceStep';
import AreaInputStep from './components/AreaInputStep';
import PaymentStep from './components/PaymentStep';
import ExtractionStep from './components/ExtractionStep';
import CompleteStep from './components/CompleteStep';
import ErrorStep from './components/ErrorStep';
import {
  uploadFileStart,
  clearFileUpload,
  resetMemberLookup,
  fetchLivePriceStart,
  registerMemberLookupStart,
  createCheckoutStart,
  confirmPostpay,
  resetPaymentCheckout,
  pollCheckoutStatusStart,
  restoreLookupId
} from 'redux/bydaMemberLookup/actions';
import {
  MemberLookupProcessingPhase,
  MemberLookupPaymentStatus,
  LivePrice
} from 'redux/bydaMemberLookup/types';
import { getDownloadUrl } from 'services/bydaMemberLookup.service';
import {
  confirmPostPaymentStart,
  clearPaymentState
} from 'redux/payment/actions';
import {
  selectPostPaymentConfirmed,
  selectConfirmingPostPayment
} from 'redux/payment/selectors';

const PRIMARY_LABELS: Record<string, string> = {
  price: 'Continue',
  'area-input': 'Continue',
  payment: 'Confirm',
  complete: 'Done'
};

const CHECKOUT_LOOKUP_ID_STORAGE_KEY = 'byda_member_lookup_checkout_id';

function setupBydaMemberLookupPage(
  dispatch: ReturnType<typeof useDispatch>,
  steps: StepDefinition[],
  jumpToStep: (index: number) => void
): void {
  dispatch(fetchLivePriceStart());

  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get('session_id');
  if (!sessionId) return;

  const cleanUrl =
    window.location.pathname +
    window.location.search
      .replace(/[?&]session_id=[^&]+/, '')
      .replace(/^&/, '?');
  window.history.replaceState({}, '', cleanUrl || window.location.pathname);

  dispatch(pollCheckoutStatusStart(sessionId));
  const completeStepIndex = steps.findIndex((s) => s.id === 'complete');
  jumpToStep(completeStepIndex);
}

const BydaMemberLookup: React.FC = () => {
  const dispatch = useDispatch();
  const memberLookupState = useSelector(
    (state: any) => state.memberLookupCache
  );
  const processingPhase: MemberLookupProcessingPhase =
    memberLookupState?.processingPhase ?? 'IDLE';
  const lookupError: string | null = memberLookupState?.error ?? null;
  const uploadError: string | null = memberLookupState?.uploadError ?? null;
  const shapeCount: number | null = memberLookupState?.shapeCount ?? null;
  const areaKm2: number | null = memberLookupState?.areaKm2 ?? null;
  const isFileValidated = processingPhase === 'FILE_VALIDATED';
  const fileValidationStats =
    shapeCount != null && areaKm2 != null ? { shapeCount, areaKm2 } : null;
  const memberCount: number | null = memberLookupState?.memberCount ?? null;
  const lookupId: number | null = memberLookupState?.lookupId ?? null;
  const livePrice: LivePrice | null = memberLookupState?.livePrice ?? null;
  const livePriceLoading: boolean =
    memberLookupState?.livePriceLoading ?? false;
  const livePriceError: string | null =
    memberLookupState?.livePriceError ?? null;
  const isLivePriceResolved = !livePriceLoading;
  const hasLivePrice = livePrice !== null;
  const clientSecret: string | null = memberLookupState?.clientSecret ?? null;
  const paymentStatus: MemberLookupPaymentStatus =
    memberLookupState?.paymentStatus ?? 'idle';
  const postPaymentId: number | null = memberLookupState?.postPaymentId ?? null;
  const transactionReference: string | null =
    memberLookupState?.transactionReference ?? null;

  const postPaymentConfirmed = useSelector(selectPostPaymentConfirmed);
  const confirmingPostPayment = useSelector(selectConfirmingPostPayment);

  const {
    activeStep,
    steps,
    inputMode,
    uploadedFile,
    drawnShapes,
    setInputMode,
    setUploadedFile,
    addDrawnShape,
    updateDrawnShape,
    removeDrawnShape,
    clearAllShapes,
    goNext,
    goBack,
    jumpToStep,
    resetWizard,
    canProceed
  } = useBydaMemberLookupWizard();

  const currentStepId = steps[activeStep]?.id;
  const [pendingContinue, setPendingContinue] = useState(false);
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState<string>('');

  useMapSketchRegistration({
    inputMode,
    drawnShapes,
    processingPhase,
    lookupId,
    pendingContinue
  });

  const isFileUploadInProgress =
    processingPhase === 'UPLOADING_FILE' ||
    processingPhase === 'VALIDATING_FILE';

  const isMemberLookupOperationActive =
    processingPhase !== 'IDLE' &&
    processingPhase !== 'FILE_UPLOAD_READY' &&
    processingPhase !== 'UPLOADING_FILE' &&
    processingPhase !== 'VALIDATING_FILE' &&
    processingPhase !== 'REGISTERING';

  /**
   * Initializes page state on mount and resets workflow when leaving.
   *
   * WHY: Must run once on mount for live pricing and Stripe return URL handling.
   * Cleanup resets Redux so in-app navigation always starts a fresh flow.
   */
  useEffect(() => {
    setupBydaMemberLookupPage(dispatch, steps, jumpToStep);
    return () => {
      dispatch(resetMemberLookup());
    };
  }, [dispatch, jumpToStep, steps]);

  /**
   * Creates checkout when the user enters Payment. Response determines prepay vs postpay UI.
   *
   * WHY: Eligibility is only known from POST /payment/checkout; must run on tab entry.
   */
  useEffect(() => {
    if (
      currentStepId === 'payment' &&
      paymentStatus === 'idle' &&
      lookupId !== null
    ) {
      dispatch(createCheckoutStart(lookupId));
    }
  }, [currentStepId, paymentStatus, lookupId, dispatch]);

  const handleWizardBack = useCallback(() => {
    if (currentStepId === 'payment') {
      dispatch(resetPaymentCheckout());
      dispatch(clearPaymentState());
      setPurchaseOrderNumber('');
    }
    goBack();
  }, [currentStepId, dispatch, goBack]);

  const handleFileSelected = useCallback(
    (file: File) => {
      setUploadedFile({
        name: file.name,
        size: formatFileSize(file.size)
      });
      dispatch(uploadFileStart({ file }));
    },
    [setUploadedFile, dispatch]
  );

  const handleFileReplace = useCallback(() => {
    setUploadedFile(null);
    dispatch(clearFileUpload());
  }, [setUploadedFile, dispatch]);

  const handleAreaInputContinue = useCallback(() => {
    if (inputMode === 'draw' && drawnShapes.length > 0) {
      if (lookupId !== null && processingPhase === 'AWAITING_PAYMENT') {
        goNext();
        return;
      }

      setPendingContinue(true);

      if (processingPhase !== 'REGISTERING') {
        dispatch(
          registerMemberLookupStart({
            inputSource: 'MAP_SKETCH',
            shapes: drawnShapes.map((s) => s.polygon)
          })
        );
      }
      return;
    }

    goNext();
  }, [dispatch, inputMode, drawnShapes, lookupId, processingPhase, goNext]);

  useEffect(() => {
    function advanceAfterPendingRegistration() {
      if (currentStepId !== 'area-input') return;

      if (
        pendingContinue &&
        lookupId !== null &&
        processingPhase === 'AWAITING_PAYMENT'
      ) {
        setPendingContinue(false);
        goNext();
        return;
      }

      if (pendingContinue && uploadError && processingPhase === 'IDLE') {
        setPendingContinue(false);
      }
    }

    advanceAfterPendingRegistration();
  }, [
    pendingContinue,
    lookupId,
    processingPhase,
    uploadError,
    currentStepId,
    goNext
  ]);

  useEffect(() => {
    if (postPaymentConfirmed) {
      dispatch(confirmPostpay());
      goNext();
    }

    return () => {
      dispatch(clearPaymentState());
    };
  }, [dispatch, postPaymentConfirmed, goNext]);

  /**
   * Stripe return lands on Complete while checkout is polled; send payment failures
   * back to the Payment step so the progress bar matches the error UI.
   */
  useEffect(() => {
    function redirectPaymentFailureToPaymentStep() {
      if (processingPhase !== 'PAYMENT_EXPIRED') return;

      const paymentStepIndex = steps.findIndex((s) => s.id === 'payment');
      if (paymentStepIndex < 0 || currentStepId === 'payment') return;

      jumpToStep(paymentStepIndex);
    }

    redirectPaymentFailureToPaymentStep();
  }, [processingPhase, currentStepId, steps, jumpToStep]);

  const handleRetryPayment = useCallback(() => {
    const storedId = sessionStorage.getItem(CHECKOUT_LOOKUP_ID_STORAGE_KEY);
    const effectiveLookupId = lookupId ?? (storedId ? Number(storedId) : null);

    if (effectiveLookupId == null || Number.isNaN(effectiveLookupId)) {
      return;
    }

    if (lookupId === null) {
      dispatch(restoreLookupId(effectiveLookupId));
    }

    dispatch(resetPaymentCheckout());
    dispatch(createCheckoutStart(effectiveLookupId));
  }, [dispatch, lookupId]);

  const handleStartOver = useCallback(() => {
    setPendingContinue(false);
    dispatch(resetMemberLookup());
    resetWizard();
  }, [dispatch, resetWizard]);

  const handleDownload = useCallback(async () => {
    if (!lookupId) return;
    try {
      const { url } = await getDownloadUrl(lookupId);
      window.location.href = url;
    } catch {
      // Download errors are non-critical — the user can retry
    }
  }, [lookupId]);

  const handleShare = useCallback(() => {
    // TODO: Implement share functionality
  }, []);

  const handleProceedToPayment = useCallback(() => {
    if (
      lookupId !== null &&
      paymentStatus === 'invoice' &&
      postPaymentId !== null
    ) {
      dispatch(confirmPostPaymentStart(postPaymentId, purchaseOrderNumber));
    }
  }, [dispatch, lookupId, paymentStatus, postPaymentId, purchaseOrderNumber]);

  const handlePrimaryAction = useCallback(() => {
    if (currentStepId === 'area-input') {
      handleAreaInputContinue();
    } else if (currentStepId === 'payment') {
      handleProceedToPayment();
    } else {
      goNext();
    }
  }, [currentStepId, handleAreaInputContinue, handleProceedToPayment, goNext]);

  function getExtractionStatus():
    | 'confirming_payment'
    | 'finding_members'
    | 'idle' {
    if (processingPhase === 'CONFIRMING_PAYMENT') return 'confirming_payment';
    if (processingPhase === 'FINDING_MEMBERS') return 'finding_members';
    return 'idle';
  }

  const effectiveCanProceed = (() => {
    if (currentStepId === 'price') {
      return isLivePriceResolved && hasLivePrice;
    }
    if (currentStepId === 'area-input') {
      if (inputMode === 'upload') return processingPhase === 'FILE_VALIDATED';
      if (inputMode === 'draw') return drawnShapes.length > 0;
      return false;
    }
    if (currentStepId === 'payment') {
      if (paymentStatus === 'invoice') {
        return lookupId !== null && isLivePriceResolved && hasLivePrice;
      }
      return false;
    }
    return canProceed;
  })();

  const isPaymentConfirmLoading =
    currentStepId === 'payment' && paymentStatus === 'creating';

  const isMapSketchContinueLoading =
    inputMode === 'draw' &&
    currentStepId === 'area-input' &&
    pendingContinue &&
    (processingPhase === 'REGISTERING' || (lookupId === null && !uploadError));

  const isLivePriceLoadingOnPriceStep =
    currentStepId === 'price' && livePriceLoading;

  const primaryActionLabel = (() => {
    if (isMapSketchContinueLoading) return 'Validating...';
    if (isPaymentConfirmLoading) return 'Preparing payment...';
    return PRIMARY_LABELS[currentStepId] || 'Continue';
  })();

  const isPrimaryActionLoading =
    isMapSketchContinueLoading ||
    isPaymentConfirmLoading ||
    isLivePriceLoadingOnPriceStep;

  const uploadStatusLabel =
    processingPhase === 'VALIDATING_FILE'
      ? 'Validating file...'
      : 'Uploading...';

  const paymentStepIndex = steps.findIndex((s) => s.id === 'payment');
  const isPaymentUnsuccessful =
    currentStepId === 'payment' &&
    paymentStatus !== 'creating' &&
    (paymentStatus === 'failed' || processingPhase === 'PAYMENT_EXPIRED');

  const renderStepContent = () => {
    switch (currentStepId) {
      case 'price':
        return (
          <PriceStep
            livePrice={livePrice}
            isLivePriceLoading={livePriceLoading}
            livePriceError={livePriceError}
          />
        );
      case 'area-input':
        return (
          <AreaInputStep
            inputMode={inputMode}
            onInputModeChange={setInputMode}
            isMemberLookupOperationActive={isMemberLookupOperationActive}
            isUploading={isFileUploadInProgress}
            uploadStatusLabel={uploadStatusLabel}
            uploadError={uploadError}
            uploadedFile={uploadedFile}
            onFileSelected={handleFileSelected}
            onFileReplace={handleFileReplace}
            drawnShapes={drawnShapes}
            onDrawingComplete={addDrawnShape}
            onShapeUpdated={updateDrawnShape}
            onClearShape={removeDrawnShape}
            onClearAllShapes={clearAllShapes}
            registerError={inputMode === 'draw' ? uploadError : null}
            isFileValidated={isFileValidated}
            fileValidationStats={fileValidationStats}
          />
        );
      case 'payment':
        if (isPaymentUnsuccessful) {
          return (
            <PaymentUnsuccessfulScreen
              onTryAgain={handleRetryPayment}
              onCancel={handleStartOver}
            />
          );
        }

        return (
          <PaymentStep
            paymentStatus={confirmingPostPayment ? 'confirming' : paymentStatus}
            livePrice={livePrice}
            isLivePriceLoading={livePriceLoading}
            clientSecret={clientSecret}
            purchaseOrderNumber={purchaseOrderNumber}
            onPurchaseOrderNumberChange={(value) =>
              setPurchaseOrderNumber(value)
            }
          />
        );
      case 'complete': {
        if (processingPhase === 'ERROR') {
          return (
            <ErrorStep error={lookupError} onStartOver={handleStartOver} />
          );
        }

        const isExtracting =
          processingPhase === 'CONFIRMING_PAYMENT' ||
          processingPhase === 'FINDING_MEMBERS';

        if (isExtracting) {
          return <ExtractionStep status={getExtractionStatus()} />;
        }

        if (processingPhase === 'COMPLETED') {
          const isPostpay = paymentStatus === 'invoice';

          return (
            <CompleteStep
              memberCount={memberCount ?? undefined}
              isPostpay={isPostpay}
              transactionReference={transactionReference ?? undefined}
              onDownload={handleDownload}
              onShare={handleShare}
              onLodgeAnother={handleStartOver}
            />
          );
        }

        return null;
      }
      default:
        return null;
    }
  };

  const showPrimaryAction =
    !isPaymentUnsuccessful &&
    currentStepId !== 'complete' &&
    !confirmingPostPayment &&
    !(currentStepId === 'payment' && paymentStatus !== 'invoice');

  const isCheckoutPhase =
    currentStepId === 'payment' &&
    paymentStatus === 'checkout' &&
    Boolean(clientSecret);
  const showBack =
    activeStep > 0 &&
    currentStepId !== 'complete' &&
    !confirmingPostPayment &&
    !isPaymentUnsuccessful &&
    !isCheckoutPhase;

  return (
    <div className="byda-member-lookup" data-testid="byda-member-lookup">
      <FullHeaderLeftSideFullPanelLayout
        pageHeading="BYDA member lookup"
        pageDescription="Find all BYDA members with assets in your project area and export their contact details"
        enableHeaderGlobalBar={true}
      >
        <WizardShell
          title="BYDA member lookup"
          subtitle="Find all BYDA members with assets in your project area and export their contact details"
          steps={steps}
          currentStepIndex={activeStep}
          isLastStepCompleted={processingPhase === 'COMPLETED'}
          errorStepIndex={isPaymentUnsuccessful ? paymentStepIndex : undefined}
          onBack={showBack ? handleWizardBack : undefined}
          showBack={showBack}
          primaryAction={{
            label: primaryActionLabel,
            onClick: handlePrimaryAction,
            disabled: !effectiveCanProceed || isPrimaryActionLoading,
            loading: isPrimaryActionLoading,
            show: showPrimaryAction
          }}
        >
          {renderStepContent()}
        </WizardShell>
      </FullHeaderLeftSideFullPanelLayout>
    </div>
  );
};

export default BydaMemberLookup;
